"use client";

import { useCallback } from "react";
import { toast } from "sonner";
import { z } from "zod";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types";
import { isZodError, getErrorMessage, formatZodErrorForUser } from "../types";

interface UseFormSubmissionProps<T extends BaseEntity> {
  form: UseFormReturn<FieldValues>;
  config: SimpleTableConfig<any>;
  entityName: string;
  isCreating: boolean;
  setIsCreating: (creating: boolean) => void;
  setSubmissionError: (error: string | null) => void;
  setHasValidationErrors: (hasErrors: boolean) => void;
  setIsSuccessfullySubmitted: (submitted: boolean) => void;
  setSelectedTemplate: (template: any) => void;
  onSubmit: (data: Partial<T>) => void | Promise<void>;
  /**
   * Returns a loose record. The cleaner is generic over the entity config and
   * cannot statically know per-field shapes (see form-data-cleaner.ts for the
   * runtime invariants). The downstream entity-specific Zod schema in the
   * mutation hook is the source of truth for the final API contract.
   */
  cleanFormData: (data: Record<string, unknown>) => Record<string, unknown>;
  extractErrorFields: (
    errors: Record<string, unknown>
  ) => Array<{ fieldLabel: string; message: string; fieldName: string }>;
  extractZodErrorFields: (
    error: any
  ) => Array<{ fieldLabel: string; message: string; fieldName: string }>;
}

/**
 * Handles form submission workflow including validation, error handling, and success state
 */
export function useFormSubmission<T extends BaseEntity = BaseEntity>({
  form,
  config,
  entityName,
  isCreating,
  setIsCreating,
  setSubmissionError,
  setHasValidationErrors,
  setIsSuccessfullySubmitted,
  setSelectedTemplate,
  onSubmit,
  cleanFormData,
  extractErrorFields,
  extractZodErrorFields,
}: UseFormSubmissionProps<T>) {
  const {
    trigger,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      if (isCreating) return; // Prevent double submission

      setIsCreating(true);
      setSubmissionError(null); // Clear any previous submission errors

      try {
        // Clean up the data before submission
        const cleanedData = cleanFormData(data);

        // The cleaner is generic; the downstream mutation hook (e.g.
        // useCreateDeal) re-validates with an entity-specific Zod schema
        // (DealsInsertInputSchema.parse). That parse is the actual safety net
        // for shape mismatches.
        await onSubmit(cleanedData as Partial<T>);

        // Reset form after successful submission
        reset();
        setSelectedTemplate(null);
        setSubmissionError(null);
        setHasValidationErrors(false);

        // Note: Success feedback is handled by parent components via toast or other means
        // We do NOT show optimistic success state here - only after server confirms
      } catch (error: unknown) {
        console.error(`Error creating ${entityName}:`, error);

        // Never expose code or stack — only user-safe field + reason
        if (isZodError(error)) {
          const safeMessage = formatZodErrorForUser(error);
          setSubmissionError(safeMessage);
          toast.error(safeMessage);
        } else {
          const errorMessage = getErrorMessage(error);
          setSubmissionError(errorMessage);
          toast.error(errorMessage);
        }
      } finally {
        setIsCreating(false);
      }
    },
    [
      isCreating,
      onSubmit,
      reset,
      entityName,
      cleanFormData,
      setIsCreating,
      setSubmissionError,
      setHasValidationErrors,
      setSelectedTemplate,
    ]
  );

  // Handle submit button click
  const handleSubmitClick = useCallback(async () => {
    try {
      // Trigger validation
      const isValid = await trigger();

      if (!isValid) {
        // Validation errors will be displayed in FormStatusAlerts via errorInfo
        // No need to show popup - errors are already visible at the top
      } else {
        // Form is valid - clear any previous error popup and submit
        await handleSubmit(handleFormSubmit)();
      }
    } catch (error: unknown) {
      if (isZodError(error)) {
        setSubmissionError(formatZodErrorForUser(error));
      } else {
        console.error("Unexpected error during validation:", error);
        setSubmissionError(getErrorMessage(error));
      }
    }
  }, [trigger, handleSubmit, handleFormSubmit, setSubmissionError]);

  return {
    handleFormSubmit,
    handleSubmitClick,
  };
}
